<template>
  <span v-if="workingReward">
    <v-row class="mt-8">
      <v-col cols="12">
        <v-text-field
          label="Max Total Refund Per Account"
          v-model="workingReward.maxTotalRefundAmountPerAccount"
          type="number"
          hint="Ex. 25.00"
          prefix="$"
          hide-spin-buttons
          dense
          :rules="optionalAndPositiveRule"
          @input="onChange"
          name="maxTotalRefundAmountPerAccount"
        />
      </v-col>
    </v-row>
    <v-row class="mt-6">
      <v-col cols="12">
        <v-text-field
          label="Max Refund Per Transaction"
          v-model="workingReward.maxRefundAmountPerTransaction"
          type="number"
          hint="Ex. 3.00"
          prefix="$"
          hide-spin-buttons
          dense
          :rules="optionalAndPositiveRule"
          @input="onChange"
          name="maxRefundAmountPerTransaction"
        />
        <div v-if="isCalcFromTransactionAmount()" id="transactionAmountCalculationMessage" class="info-text">
          {{transactionAmountCalculationMessage()}}
        </div>
      </v-col>
    </v-row>

    <v-row class="mt-8">
      <v-col cols="12">
        <v-text-field
          label="Transaction Count Limit"
          v-model="workingReward.transactionCountLimit"
          type="number"
          hint="Ex. 4"
          hide-spin-buttons
          dense
          :rules="optionalAndPositiveRule"
          @input="onChange"
          name="transactionCountLimit"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-checkbox label="Refund Terminal Fee" v-model="workingReward.refundTerminalFee" dense hide-details :rules="[checkBoxValidation]" @change="onFeeRuleChange(); onChange()" ref="refundTerminalFee"/>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-checkbox label="Refund Foreign Atm Usage Fee" v-model="workingReward.refundForeignAtmUsageFee" hide-details dense :rules="[checkBoxValidation]" @change="onFeeRuleChange(); onChange()" ref="refundForeignAtmUsageFee"/>
      </v-col>
    </v-row>

    <v-row v-if="checkBoxValidation!==false && checkBoxValidation!==true" ref= "checkBoxValidationMessage">
      <v-col cols="12" class="red--text">
        {{checkBoxValidation}}
      </v-col>
    </v-row>
  </span>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Prop, Watch,
} from 'vue-property-decorator';
import { AtmReward } from '@/api/product/levelrewards/rewards';
import { ValidationTool } from '@/util';
import { ATM, ATMTerminalFeeCalculationMethods, ATMTerminalFeeModulus } from '@/api/atmsettings';

@Component
export default class AtmRewardEditor extends Vue {
  @Prop()
  value!:AtmReward;

  @Prop()
  atmSetting!: ATM | null;

  workingReward:AtmReward | null = null;

  // "False" puts the checkboxes in an "invalid" state, since we need at least one to be selected, but doesn't show the error yet.
  // "True" tells them that they are valid.  Any string indicates invalid but show the message.
  checkBoxValidation:boolean|string = false;

  requiredAndPositiveRule:Array<any> = [ValidationTool.requiredAndPositiveRuleFunction];

  optionalAndPositiveRule:Array<any> = [(value: number) => {
    if (value < 0) {
      return 'Must be positive';
    }
    return true;
  }];

  transactionAmountCalculationMessage(): string {
    if (!this.isCalcFromTransactionAmount()) {
      return '';
    }

    let modulus: number;
    if (this.atmSetting!.terminalFeeModulus === ATMTerminalFeeModulus.TEN_DOLLARS) {
      modulus = 10;
    } else {
      modulus = 5;
    }

    return `FI is currently configured to use MOD ${modulus}, therefore the current system effective cap is ${modulus - 0.01}`;
  }

  isCalcFromTransactionAmount(): boolean {
    if (!this.atmSetting) {
      return false;
    }

    return this.atmSetting.terminalFeeCalculationMethod === ATMTerminalFeeCalculationMethods.CALCULATE_FROM_TRANSACTION_AMOUNT;
  }

  onFeeRuleChange() {
    if (!this.isAtLeastOneFeeRuleSelected()) {
      this.checkBoxValidation = 'Must select at least one atm fee type to refund.';
    } else {
      this.checkBoxValidation = true;
    }
  }

  mounted() {
    this.onExternalRewardValueChanges();
    this.initializeValidation();
  }

  @Watch('value')
  onExternalRewardValueChanges() {
    this.workingReward = this.value.clone() as AtmReward;
  }

  onChange() {
    if ((this.workingReward as any)?.maxRefundAmountPerTransaction === '') {
      // STELLA-2357 - This field should be submitted as null not empty string
      this.workingReward!.maxRefundAmountPerTransaction = null;
    }
    this.$emit('input', this.workingReward);
  }

  initializeValidation() {
    this.checkBoxValidation = this.isAtLeastOneFeeRuleSelected();
  }

  isAtLeastOneFeeRuleSelected(): boolean {
    return this.workingReward !== null && (this.workingReward.refundTerminalFee || this.workingReward.refundForeignAtmUsageFee);
  }
}
</script>

<style lang="scss" scoped>
.info-text {
  color: #1565C0;
  opacity: .85;
  padding-top: 0px;
  margin-top: 0px;
}
</style>
